import { api } from '../api/client.js';
import { requiresTroubleshootingConfirmation } from './skip-2fa-reasons.js';

/**
 * Client seam for the REGISTRATION screens' ECL-only "reason for
 * non-completion" (skip-2FA) flow — issue #534. Mirrors the Medium Security
 * fallback trio (#403) exactly:
 *
 *   beginRegistrationSecurity   → POST /security/registration/begin
 *   recordRegistrationSkip2fa   → POST /security/registration/skip-2fa
 *   completeRegistrationSecurity → POST /security/registration/complete
 *
 * The registration 2FA step (SCRN-17..20, CCF-8/46/57/58/59) normally validates
 * an OTP via `/security/otp/validate`, which raises the server-side session to
 * `medium` — and the registration self-service transfer
 * (`/genesys/transfer-to-registration`) is PAN-gated, so the skip path must
 * raise the session the same way or the transfer would 403. All three routes
 * are gated by the DEDICATED, ECLAgent-only `skipRegistration2fa` permission
 * (the tester ruling is that ONLY ECL agents get the dropdown), bound to the
 * identified customer, reason-allow-listed and audited; completion reads ONLY
 * the server-stored skip marker, never a client-supplied reason.
 *
 * Every helper is fail-closed from the UI's perspective: a non-OK response (or
 * a network error) returns `false` and the caller must NOT treat 2FA as passed
 * / must abort the transfer.
 */

/**
 * Begin a server-side Registration Security run for the identified customer.
 * The registration screens call this when the 2FA step is available to an ECL
 * agent, so a later sanctioned skip is bound to a genuine registration flow —
 * a bare identified session cannot elevate via `complete` directly.
 *
 * Best-effort: the server still fail-closes at skip/complete if the run was
 * not recorded, so a failure here only blocks the skip path (the OTP path is
 * unaffected).
 *
 * @returns `true` when the server started the run; `false` otherwise.
 */
export async function beginRegistrationSecurity(
  customerNumber: string,
  tellerId: string,
): Promise<boolean> {
  try {
    const { data, response } = await api.POST('/security/registration/begin', {
      params: { header: { 'X-Teller-Id': tellerId } },
      body: { customerNumber },
    });
    return response.ok && data?.started === true;
  } catch {
    return false;
  }
}

/**
 * Record a sanctioned 2FA skip against the active Registration Security run.
 * Called as soon as the ECL agent selects a "reason for non-completion" (for
 * the TL-exemption reason, once the required team lead's name is supplied —
 * #586 extension) — the 2FA piece is treated as passed ONLY when this returns
 * `true` (fail-closed: a refusal — wrong role, no active run, unsanctioned
 * reason, missing/invalid team lead name — restores the OTP requirement).
 *
 * @returns `true` when the server recorded the skip; `false` otherwise.
 */
export async function recordRegistrationSkip2fa(
  customerNumber: string,
  tellerId: string,
  reason: string,
  /**
   * #586: the approving team lead's name — forwarded only for the
   * "2FA Exemption Approved by TL" reason; the server independently requires
   * + validates it for that reason (never trusts the client).
   */
  teamLeadName?: string,
  /**
   * #789: the "Troubleshooting completed" attestation — forwarded only for the
   * "2FA Customer Device Issue" reason; the server independently requires it for
   * that reason (never trusts the client — the checkbox is UX only).
   */
  troubleshootingCompleted?: boolean,
): Promise<boolean> {
  try {
    const { data, response } = await api.POST(
      '/security/registration/skip-2fa',
      {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          reason,
          ...(teamLeadName ? { teamLeadName } : {}),
          ...(requiresTroubleshootingConfirmation(reason)
            ? { troubleshootingCompleted: troubleshootingCompleted === true }
            : {}),
        },
      },
    );
    return response.ok && data?.recorded === true;
  } catch {
    return false;
  }
}

/**
 * Commit the registration 2FA skip: consumes the server-stored skip marker on
 * the active run and raises the session to `medium` (mirroring what
 * `/security/otp/validate` does on the OTP path), so the PAN-gated
 * registration transfer succeeds. Called from the transfer action — the
 * explicit "commit" point — never speculatively, so clearing the reason
 * selection before transferring leaves the server session untouched.
 *
 * @returns `true` when the server recorded `medium`; `false` otherwise.
 */
export async function completeRegistrationSecurity(
  customerNumber: string,
  tellerId: string,
): Promise<boolean> {
  try {
    const { data, response } = await api.POST(
      '/security/registration/complete',
      {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber },
      },
    );
    return response.ok && data?.level === 'medium';
  } catch {
    return false;
  }
}

/**
 * Ask the BFF whether 2FA is ALREADY SATISFIED for the identified customer
 * earlier in this session (#737) — the registration analogue of the medium→high
 * carryover (#412 rule, #456). When this returns `true` the registration
 * screens' CCF-16 2FA step renders as already-completed ("2FA already completed
 * on this call.") instead of asking the agent for a fresh OTP.
 *
 * The verdict is SERVER-DERIVED ONLY: the BFF computes it from the session's
 * explicit 2FA markers (a validated OTP bound to the customer, or the consumed
 * sanctioned #534 registration skip), bound to the customer the session is
 * identified with and invalidated by a terminal #639 per-CIF OTP lockout. A
 * client-side claim never satisfies it, and this call records/elevates nothing
 * — the completion + IPW-attestation routes keep independently re-verifying
 * their own server-stored evidence.
 *
 * Fail-closed: a non-OK response (or a network error) returns `false`, so the
 * caller must present the 2FA step normally.
 *
 * #774: the SAME server-derived read also reports whether the ACTIVE
 * Registration Security run carries the agent-recorded 2FA FAIL marker (the
 * Mortgage "Unable to complete 2FA" tick box) — the re-hydration source for a
 * screen re-mount, never a client-side claim.
 */
export async function checkRegistration2faStatus(
  customerNumber: string,
  tellerId: string,
): Promise<{ satisfied: boolean; twoFaFailRecorded: boolean }> {
  try {
    const { data, response } = await api.POST(
      '/security/registration/2fa-status',
      {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber },
      },
    );
    if (!response.ok) return { satisfied: false, twoFaFailRecorded: false };
    return {
      satisfied: data?.satisfied === true,
      twoFaFailRecorded: data?.twoFaFailRecorded === true,
    };
  } catch {
    return { satisfied: false, twoFaFailRecorded: false };
  }
}

/**
 * BEGIN the server-side Registration Security run for the #774 Mortgage
 * "Unable to complete 2FA" tick box — the NON-ECL twin of
 * `beginRegistrationSecurity` (#774 reopen). The tick box is for the agents
 * who run mortgage registration (NOT ECL-scoped), so the run the fail is
 * recorded on is begun through the dedicated `recordRegistration2faFail`
 * permission instead of the ECLAgent-only #534 seam. NOT elevation-capable on
 * its own: the skip/complete routes stay `skipRegistration2fa`-only (ECL), so
 * a non-ECL role that begins here can record ONLY the fail marker (a fail
 * never elevates).
 *
 * Fail-closed: a non-OK response (or a network error) returns `false` and the
 * caller must NOT record a fail against a run that does not exist.
 *
 * @returns `true` when the server started the run; `false` otherwise.
 */
export async function beginRegistrationFail2fa(
  customerNumber: string,
  tellerId: string,
): Promise<boolean> {
  try {
    const { data, response } = await api.POST(
      '/security/registration/begin-fail-2fa',
      {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber },
      },
    );
    return response.ok && data?.started === true;
  } catch {
    return false;
  }
}

/**
 * Record a server-side 2FA FAIL against the active Registration Security run
 * (#774, @dylan219) — the Mortgage registration "Unable to complete 2FA" tick
 * box beside Send Code. The fail lives SERVER-SIDE on the session-bound,
 * CIF-bound run (never a client-declared flag — the #729 lesson), exactly like
 * the #425 broker attest records the overall outcome on its run. The fail
 * outcome/messaging is DEFERRED client-side until every other question is
 * answered (the #781 deferral), and the marker can NEVER elevate the session:
 * the completion/skip/IPW-attestation routes read only the sanctioned skip /
 * OTP markers.
 *
 * Fail-closed from the UI's perspective: a non-OK response (or a network
 * error) returns `false` and the caller must NOT assert the tick.
 */
export async function recordRegistrationFail2fa(
  customerNumber: string,
  tellerId: string,
): Promise<boolean> {
  try {
    const { data, response } = await api.POST(
      '/security/registration/fail-2fa',
      {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber },
      },
    );
    return response.ok && data?.recorded === true;
  } catch {
    return false;
  }
}

/**
 * Clear the server-side 2FA FAIL marker (#774) when the agent unticks
 * "Unable to complete 2FA" so an un-ticked box never leaves a stale
 * server-side fail suppressing the OTP path. Fail-closed: a non-OK response
 * returns `false` and the caller must keep the tick asserted (the server
 * record still stands) rather than silently restoring the OTP path.
 */
export async function clearRegistrationFail2fa(
  customerNumber: string,
  tellerId: string,
): Promise<boolean> {
  try {
    const { data, response } = await api.POST(
      '/security/registration/clear-fail-2fa',
      {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber },
      },
    );
    return response.ok && data?.recorded === true;
  } catch {
    return false;
  }
}

/**
 * BEGIN the server-side REGISTRATION → IPW-reset attestation run (issue #653;
 * all five registration screens SCRN-17..21 call this since #729) so the
 * elevation is bound to a genuine, CIF-bound, screen-code-allow-listed
 * registration flow — a manipulated client cannot skip straight to
 * `complete-ipw`. The server records a run keyed per-CIF; `complete-ipw`
 * requires it (plus a recorded attested pass).
 *
 * Fail-closed: a non-OK response (or a network error) returns `false`.
 *
 * @returns `true` when the server started the run; `false` otherwise.
 */
export async function beginRegistrationIpw(
  customerNumber: string,
  tellerId: string,
  screenCode: string,
): Promise<boolean> {
  try {
    const { data, response } = await api.POST(
      '/security/registration/begin-ipw',
      {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, screenCode },
      },
    );
    return response.ok && data?.started === true;
  } catch {
    return false;
  }
}

/**
 * ATTEST server-side that every registration security question (+ 2FA) has
 * passed, against the ACTIVE run (issue #653). Called ONLY when `securityPass`
 * is genuinely true on the registration screen. The server records the
 * attestation on the run (`questionsAttestedAt`) — NOT a client boolean;
 * `complete-ipw` reads that server marker, never a `securityPass` flag.
 *
 * Fail-closed: a non-OK response (or a network error) returns `false`.
 *
 * @returns `true` when the server recorded the attestation; `false` otherwise.
 */
export async function attestRegistrationQuestions(
  customerNumber: string,
  tellerId: string,
  screenCode: string,
): Promise<boolean> {
  try {
    const { data, response } = await api.POST(
      '/security/registration/attest-questions',
      {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, screenCode },
      },
    );
    return response.ok && data?.attested === true;
  } catch {
    return false;
  }
}

/**
 * Commit REGISTRATION completion to authorise the Reset Internet Password write
 * from a registration page — all five registration screens (SCRN-17..21) since
 * #729; originally Business24 / Broker24 (issue #653, @dylan219) — the
 * registration analogue of the broker-security → IPW elevation (#568). Called
 * when, having passed every registration security question (the client-side
 * `securityPass`), the agent opts to reset the internet password.
 *
 * SERVER-SIDE FAIL-CLOSED (ai-review HIGH fix): this drives the REAL server
 * steps in order — `begin-ipw` (records a CIF-bound, screen-code-allow-listed
 * run) → `attest-questions` (records the server-side attested pass on that run)
 * → `complete-ipw` (requires the attested run, then records the durable,
 * CIF-bound `registrationComplete` marker honoured ONLY by the
 * `/security/internet-password` gate, and raises the session to `medium`). The
 * server never raises `medium` from an identified session + client flag alone;
 * a fresh direct `complete-ipw` with no begun + attested run is rejected. Each
 * step is fail-closed: any non-OK response aborts (returns `false`), so the
 * caller must NOT navigate to the reset page.
 *
 * @returns `true` when the server recorded `medium`; `false` otherwise.
 */
export async function completeRegistrationForIpw(
  customerNumber: string,
  tellerId: string,
  screenCode: string,
): Promise<boolean> {
  try {
    // 1. Begin the server-side run (CIF-bound + screen-code allow-listed).
    if (!(await beginRegistrationIpw(customerNumber, tellerId, screenCode))) {
      return false;
    }
    // 2. Attest server-side that the registration questions passed.
    if (
      !(await attestRegistrationQuestions(customerNumber, tellerId, screenCode))
    ) {
      return false;
    }
    // 3. Complete: require the attested run, raise medium + record the marker.
    const { data, response } = await api.POST(
      '/security/registration/complete-ipw',
      {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, screenCode },
      },
    );
    return response.ok && data?.level === 'medium';
  } catch {
    return false;
  }
}
